import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "common/redux/core/root.reducer";
import { toSafeObjectKey } from "common/utils/object";

import { homeModuleActions } from "modules/home/redux/home";
import { useFetchClientLogosMutation } from "modules/home/redux/mutations";
import { getBase64WithImgMimeType } from "modules/home/util/getClientLogo";

const DEBOUNCE_MS = 150;
const ROOT_MARGIN = "0px 0px 300px 0px";

const useBatchLogoPrefetch = () => {
  const dispatch = useAppDispatch();
  const clientLogos = useAppSelector((state) => state.home.clientLogos);
  const [fetchClientLogos] = useFetchClientLogosMutation();

  const tileMapRef = useRef<Map<string, { fileName: string; el: Element }>>(
    new Map()
  );
  const pendingSetRef = useRef<
    Map<string, { clientId: string; fileName: string }>
  >(new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const observerRef = useRef<IntersectionObserver>();
  const inFlightRef = useRef<Set<string>>(new Set());
  const clientLogosRef = useRef(clientLogos);

  // Keep a ref in sync so the observer callback always reads the latest cache
  useEffect(() => {
    clientLogosRef.current = clientLogos;
  }, [clientLogos]);

  const flushBatch = useCallback(async () => {
    const logos = Array.from(pendingSetRef.current.values());
    if (!logos.length) return;

    // Mark as in-flight and remove from pending to prevent duplicate calls
    logos.forEach(({ clientId }) => {
      pendingSetRef.current.delete(clientId);
      inFlightRef.current.add(clientId);
    });

    try {
      const result = await fetchClientLogos({ logos }).unwrap();
      const logoBatch: Record<string, string> = {};

      result.forEach(({ clientId, stream }) => {
        if (stream) {
          logoBatch[clientId] = getBase64WithImgMimeType(stream);
        }
        // Unobserve tile regardless (fetched or empty stream)
        const tile = tileMapRef.current.get(clientId);
        if (tile?.el) {
          observerRef.current?.unobserve(tile.el);
        }
        inFlightRef.current.delete(clientId);
      });

      if (Object.keys(logoBatch).length) {
        dispatch(homeModuleActions.updateClientLogos(logoBatch));
      }
    } catch (err) {
      // Re-add failed IDs so a future scroll event can re-trigger them
      logos.forEach((logo) => {
        inFlightRef.current.delete(logo.clientId);
        pendingSetRef.current.set(logo.clientId, logo);
      });
      console.error("Batch logo fetch failed:", err);
    }
  }, [fetchClientLogos, dispatch]);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const clientId = entry.target.getAttribute("data-client-id");
        if (!clientId) return;

        if (
          entry.isIntersecting &&
          !clientLogosRef.current[toSafeObjectKey(clientId)] &&
          !inFlightRef.current.has(clientId)
        ) {
          const tile = tileMapRef.current.get(clientId);
          pendingSetRef.current.set(clientId, {
            clientId,
            fileName: tile?.fileName || ""
          });
        } else if (!entry.isIntersecting) {
          pendingSetRef.current.delete(clientId);
        }
      });

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(flushBatch, DEBOUNCE_MS);
    },
    [flushBatch]
  );

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null, // since layout is predictable, we can use the viewport as the root
      rootMargin: ROOT_MARGIN,
      threshold: 0
    });

    // Observe any tiles already registered
    tileMapRef.current.forEach(({ el }, clientId) => {
      if (!clientLogosRef.current[toSafeObjectKey(clientId)]) {
        observerRef.current?.observe(el);
      }
    });

    return () => {
      observerRef.current?.disconnect();
      clearTimeout(debounceRef.current);
    };
  }, [handleIntersection]);

  /**
   * Ref callback factory — returns a callback ref to attach on each tile.
   * Usage: <ClientCard ref={createTileRef(client.id, client.clientIconFileName)} />
   */
  const createTileRef = useCallback(
    (clientId: string, fileName?: string) => (el: HTMLDivElement | null) => {
      const prev = tileMapRef.current.get(clientId);

      if (!el) {
        // Tile unmounted — clean up
        if (prev?.el) {
          observerRef.current?.unobserve(prev.el);
        }
        tileMapRef.current.delete(clientId);
        pendingSetRef.current.delete(clientId);
        return;
      }

      // Unobserve previous element if it changed
      if (prev?.el && prev.el !== el) {
        observerRef.current?.unobserve(prev.el);
      }

      tileMapRef.current.set(clientId, { fileName: fileName || "", el });

      if (!clientLogosRef.current[toSafeObjectKey(clientId)] && observerRef.current) {
        observerRef.current.observe(el);
      }
    },
    []
  );

  return { createTileRef };
};

export default useBatchLogoPrefetch;
